import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  InjectionToken,
  OnDestroy,
  Renderer2,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import {
  MODERN_FRAME_PROTOCOL,
  MODERN_FRAME_VERSION,
  createModernFrameId,
  getModernFramePathname,
  isAllowedModernFrameRoute,
  isSafeShellRoute,
  parseModernFrameHelloEventV2,
  parseModernFrameToHostMessageV2,
  resolveModernFramePagePath,
  toCanonicalModernUrl,
  toModernFrameUrl,
  type HostToModernFrameMessageV2,
  type ModernFrameAccessResultV2,
  type ModernFrameNavigationTriggerV2,
  type ModernFrameToHostMessageV2,
} from '@platform/contracts';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth/authService.service';
import { ActivityService } from '../../services/auth/activityService.service';
import { CurrentUserContextService } from '../../services/auth/current-user-context.service';
import { type PageAccessResult } from '../../services/auth/page-access.service';
import { RoutePageAccessPolicy } from '../../services/auth/route-page-access.policy';
import { PageTitleService } from '../services/page-title.service';
import { MODERN_FRAME_DEFAULT_ROUTE } from '../routing/modern-frame-routing';
import { ModernFramePresentationService } from './modern-frame-presentation.service';
import { ModernFrameSessionService } from './modern-frame-session.service';

type FrameFailurePhase = 'document' | 'handshake' | 'navigation' | 'authorization' | 'frame';

export interface ModernFrameTimings {
  readonly documentLoadTimeoutMs: number;
  readonly readyTimeoutMs: number;
  readonly handshakeTimeoutMs?: number;
  readonly loadingDelayMs?: number;
  readonly activeRetryDelayMs?: number;
  /** Overrides background engine recovery backoff for unit tests. */
  readonly backgroundRetryDelaysMs?: readonly number[];
}

export const MODERN_FRAME_TIMINGS = new InjectionToken<ModernFrameTimings>('MODERN_FRAME_TIMINGS', {
  providedIn: 'root',
  factory: () => ({
    documentLoadTimeoutMs: environment.modernFrameDocumentLoadTimeoutMs,
    readyTimeoutMs: 5_000,
    handshakeTimeoutMs: 5_000,
    loadingDelayMs: 150,
    activeRetryDelayMs: 0,
  }),
});

export const MODERN_FRAME_CHANNEL_FACTORY = new InjectionToken<() => MessageChannel>(
  'MODERN_FRAME_CHANNEL_FACTORY',
  { providedIn: 'root', factory: () => () => new MessageChannel() },
);

const MAX_ACTIVE_RETRIES = 1;
const BACKGROUND_RETRY_DELAYS_MS = [5_000, 10_000, 20_000, 40_000, 60_000] as const;
const DEFAULT_FRAME_TITLE = 'Application content';
const SHELL_VIEWPORT_TOP_INSET_PX = 50;

@Component({
  selector: 'app-modern-host',
  standalone: true,
  templateUrl: './modern-host.component.html',
  styleUrl: './modern-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.modern-host--active]': 'active()',
    '[class.modern-host--overlay-active]': 'overlayActive()',
    '[attr.aria-hidden]': 'active() ? null : \'true\'',
    '[attr.inert]': 'active() ? null : \'\'',
  },
})
export class ModernHostComponent implements AfterViewInit, OnDestroy {
  readonly #activity = inject(ActivityService);
  readonly #auth = inject(AuthService);
  readonly #createChannel = inject(MODERN_FRAME_CHANNEL_FACTORY);
  readonly #currentUser = inject(CurrentUserContextService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #host: ElementRef<HTMLElement> = inject(ElementRef);
  readonly #pageTitle = inject(PageTitleService);
  readonly #presentation = inject(ModernFramePresentationService);
  readonly #routeAccessPolicy = inject(RoutePageAccessPolicy);
  readonly #renderer = inject(Renderer2);
  readonly #router = inject(Router);
  readonly #session = inject(ModernFrameSessionService);
  readonly #timings = inject(MODERN_FRAME_TIMINGS);
  readonly #title = inject(Title);

  @ViewChild('modernFrame', { static: true }) private frame!: ElementRef<HTMLIFrameElement>;

  protected readonly active = signal(false);
  protected readonly engineReady = signal(false);
  protected readonly frameTitle = signal(DEFAULT_FRAME_TITLE);
  protected readonly frameSrc = signal<string | null>(null);
  protected readonly overlayActive = signal(false);
  protected readonly revealed = signal(false);
  protected readonly routeReady = signal(false);
  protected readonly sessionReady = signal(false);
  protected readonly showLoading = signal(false);

  #activeFailureCount = 0;
  #activeNavigationId: string | null = null;
  #activeOverlayIds = new Set<string>();
  #authenticated = false;
  #authorizationInFlightFor: string | null = null;
  #authorizationSubscription?: Subscription;
  #backgroundFailureCount = 0;
  #deactivationId: string | null = null;
  #desiredRoute = MODERN_FRAME_DEFAULT_ROUTE;
  #documentLoaded = false;
  #documentLoadTimeoutId?: number;
  #frameInstanceId: string | null = null;
  #handshakeTimeoutId?: number;
  #hostSessionId: string | null = null;
  #loadingTimeoutId?: number;
  #port: MessagePort | null = null;
  #recoveryTimeoutId?: number;
  #removeMessageListener?: () => void;
  #removeOnlineListener?: () => void;
  #retryTimeoutId?: number;
  #routeReadyTimeoutId?: number;
  #sessionEpoch = 0;
  #sessionSentForHostSession: string | null = null;
  #viewReady = false;
  #waitingForOnline = false;
  #currentNavigationTrigger: ModernFrameNavigationTriggerV2 = 'imperative';
  #wasRevealedBeforeActivation = false;

  constructor() {
    this.#router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        const trigger = this.#router.getCurrentNavigation()?.trigger;
        if (trigger === 'popstate' || trigger === 'hashchange' || trigger === 'imperative') {
          this.#currentNavigationTrigger = trigger;
        }
      });

    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((event) => this.#handleParentNavigation(event.urlAfterRedirects));

    this.#auth.loginCompleted$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe((authenticated) => this.#handleAuthenticationChange(authenticated));

    this.#currentUser.ready$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#syncSessionContext();
        this.#tryActivateCurrentRoute();
      });

    this.#currentUser.status$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe((status) => {
        if (status !== 'failed' || !this.#authenticated || !this.active()) return;
        this.#handleActiveFailure('CURRENT_USER_UNAVAILABLE', 'authorization', () => {
          this.#currentUser.retry();
        });
      });

    this.#session.logout$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((reason) => this.#endAuthenticatedSession(reason));

    this.#session.sessionReplaced$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#replaceHostSession());
  }

  ngAfterViewInit(): void {
    this.#removeMessageListener = this.#renderer.listen(
      'window',
      'message',
      (event: MessageEvent<unknown>) => this.#handleWindowMessage(event),
    );
    this.#removeOnlineListener = this.#renderer.listen('window', 'online', () => this.#handleOnline());
    this.#viewReady = true;
    this.#mark('modern-frame-shell-host-ready');
    this.#handleParentNavigation(this.#router.url);
    this.#ensureEngine();
  }

  ngOnDestroy(): void {
    this.#cancelAuthorizationSubscription();
    this.#clearAllTimers();
    this.#clearOverlays();
    this.#closePort();
    this.#removeMessageListener?.();
    this.#removeOnlineListener?.();
  }

  protected onFrameLoad(): void {
    if (this.frameSrc() === null) return;
    this.#documentLoaded = true;
    this.#clearDocumentLoadTimeout();
    if (!this.engineReady() && this.#frameInstanceId === null) {
      this.#startHandshakeTimeout();
    }
  }

  protected onFrameError(): void {
    if (this.frameSrc() !== null) {
      this.#handleEngineFailure('DOCUMENT_LOAD_FAILED', 'document');
    }
  }

  #handleParentNavigation(value: string): void {
    const canonicalRoute = toCanonicalModernUrl(value);
    const isModernRoute = environment.modernFrameRoutesEnabled &&
      canonicalRoute !== null && isAllowedModernFrameRoute(canonicalRoute);
    const wasActive = this.active();

    if (!isModernRoute) {
      if (wasActive) this.#deactivateRoute();
      this.active.set(false);
      this.#presentation.setModernActive(false);
      this.#resetRoutePresentation();
      this.#pageTitle.refreshFromCurrentRoute();
      this.#ensureEngine();
      return;
    }

    this.active.set(true);
    this.#presentation.setModernActive(true);

    if (this.#isWarmFrame()) {
      const previous = this.#desiredRoute;
      const next = canonicalRoute;
      const samePathname = getModernFramePathname(previous) === getModernFramePathname(next);

      if (samePathname && next !== previous) {
        if (this.#currentNavigationTrigger === 'imperative') {
          this.#syncQueryOnlyRoute(next);
        } else {
          this.#syncQueryOnlyRouteViaActivation(next);
        }
        return;
      }

      if (!samePathname) {
        const pagePath = resolveModernFramePagePath(next);
        if (pagePath && this.#routeAccessPolicy.hasAuthorizedGrant(pagePath)) {
          this.#activateWarmPathnameRoute(next);
          return;
        }
      }
    }

    if (this.#isEngineWarm()) {
      const pagePath = resolveModernFramePagePath(canonicalRoute);
      if (pagePath && this.#routeAccessPolicy.hasAuthorizedGrant(pagePath)) {
        this.#activateResumeRoute(canonicalRoute);
        return;
      }
    }

    this.#activateColdRoute(canonicalRoute);
  }

  #isWarmFrame(): boolean {
    return this.active() && this.routeReady() && this.revealed();
  }

  #isEngineWarm(): boolean {
    return this.engineReady() && this.sessionReady() && this.frameSrc() !== null;
  }

  #syncQueryOnlyRoute(next: string): void {
    this.#desiredRoute = next;
  }

  #syncQueryOnlyRouteViaActivation(next: string): void {
    this.#activateWarmPathnameRoute(next);
  }

  #activateWarmPathnameRoute(next: string): void {
    this.#desiredRoute = next;
    this.#activeNavigationId = createModernFrameId();
    this.#tryActivateCurrentRoute();
  }

  #activateResumeRoute(canonicalRoute: string): void {
    this.#desiredRoute = canonicalRoute;
    this.#activeFailureCount = 0;
    this.#authorizationInFlightFor = null;
    this.#activeNavigationId = createModernFrameId();
    this.frameTitle.set(DEFAULT_FRAME_TITLE);
    this.routeReady.set(false);
    this.revealed.set(false);
    this.#beginLoadingPresentation();
    this.#mark(this.#navigationMark('intent'));
    this.#cancelBackgroundRecovery();
    this.#ensureEngine();
    this.#syncSessionContext();
    this.#tryActivateCurrentRoute();
  }

  #activateColdRoute(canonicalRoute: string): void {
    this.#desiredRoute = canonicalRoute;
    this.#activeFailureCount = 0;
    this.#authorizationInFlightFor = null;
    this.#activeNavigationId = createModernFrameId();
    this.frameTitle.set(DEFAULT_FRAME_TITLE);
    this.routeReady.set(false);
    this.revealed.set(false);
    this.#beginLoadingPresentation();
    this.#mark(this.#navigationMark('intent'));

    const interruptedRecovery = this.#recoveryTimeoutId !== undefined || this.#waitingForOnline;
    this.#cancelBackgroundRecovery();
    if (interruptedRecovery || this.frameSrc() === null) {
      this.#reloadDocument();
      return;
    }

    this.#ensureEngine();
    this.#syncSessionContext();
    this.#tryActivateCurrentRoute();
  }

  #handleAuthenticationChange(authenticated: boolean): void {
    const wasAuthenticated = this.#authenticated;
    this.#authenticated = authenticated;
    if (!authenticated) {
      if (wasAuthenticated) this.#endAuthenticatedSession('session-ended');
      return;
    }

    this.#syncSessionContext();
    this.#tryActivateCurrentRoute();
  }

  #endAuthenticatedSession(reason: string): void {
    const envelope = this.#channelEnvelope();
    if (envelope) {
      if (this.active()) this.#postDeactivate();
      this.#post({ ...envelope, type: 'LOGOUT', payload: { reason } });
    }

    this.#authenticated = false;
    this.#sessionEpoch += 1;
    this.#routeAccessPolicy.clearGrants();
    this.#authorizationInFlightFor = null;
    this.sessionReady.set(false);
    this.#sessionSentForHostSession = null;
    this.active.set(false);
    this.#resetRoutePresentation();
    this.#clearRouteTimers();
    this.#reconnectHealthyFrame();
  }

  #replaceHostSession(): void {
    this.#clearOverlays();
    this.#sessionEpoch += 1;
    this.#routeAccessPolicy.clearGrants();
    this.#authorizationInFlightFor = null;
    this.sessionReady.set(false);
    this.#sessionSentForHostSession = null;
    this.routeReady.set(false);
    this.revealed.set(false);
    if (this.active()) {
      this.#activeNavigationId = createModernFrameId();
      this.#beginLoadingPresentation();
    }
    this.#reconnectHealthyFrame();
  }

  #syncSessionContext(): void {
    const user = this.#currentUser.snapshot();
    const envelope = this.#channelEnvelope();
    if (!this.#authenticated || user === null || envelope === null || !this.engineReady()) return;
    if (this.#sessionSentForHostSession === envelope.hostSessionId) return;

    this.#post({ ...envelope, type: 'SESSION_CONTEXT', payload: { user } });
    this.#sessionSentForHostSession = envelope.hostSessionId;
    this.sessionReady.set(true);
  }

  #ensureEngine(): void {
    if (!environment.modernFrameRoutesEnabled || !this.#viewReady || this.engineReady()) return;
    if (this.frameSrc() === null) this.#assignFrameEntryUrl();
  }

  #assignFrameEntryUrl(): void {
    if (!navigator.onLine) {
      this.#waitingForOnline = true;
      return;
    }
    const source = toModernFrameUrl('/');
    if (source === null) return;

    this.#clearOverlays();
    this.#documentLoaded = false;
    this.#frameInstanceId = null;
    this.engineReady.set(false);
    this.sessionReady.set(false);
    this.#sessionSentForHostSession = null;
    this.frameSrc.set(source);
    this.frame.nativeElement.setAttribute('src', source);
    this.#mark('modern-frame-engine-start');
    this.#startDocumentLoadTimeout();
  }

  #reloadDocument(): void {
    if (!environment.modernFrameRoutesEnabled || !this.#viewReady) return;
    this.#clearOverlays();
    this.#clearEngineTimers();
    this.#closePort();
    this.#frameInstanceId = null;
    this.#hostSessionId = null;
    this.#documentLoaded = false;
    this.engineReady.set(false);
    this.sessionReady.set(false);
    this.#sessionSentForHostSession = null;
    this.#clearFrameSource();
    this.#retryTimeoutId = window.setTimeout(() => {
      this.#retryTimeoutId = undefined;
      this.#assignFrameEntryUrl();
    }, 0);
  }

  #handleWindowMessage(event: MessageEvent<unknown>): void {
    const frameWindow = this.frame?.nativeElement.contentWindow;
    if (!frameWindow) return;
    const hello = parseModernFrameHelloEventV2(event, {
      expectedOrigin: window.location.origin,
      expectedSource: frameWindow,
    });
    if (hello === null) return;

    if (this.#frameInstanceId === hello.frameInstanceId && this.engineReady()) return;
    this.#frameInstanceId = hello.frameInstanceId;
    this.#clearDocumentLoadTimeout();
    this.#clearHandshakeTimeout();
    this.#mark('modern-frame-hello');
    this.#measure(
      'modern-frame-shell-startup-to-hello',
      'modern-frame-shell-host-ready',
      'modern-frame-hello',
    );
    this.#connectFrame(hello.frameInstanceId);
  }

  #connectFrame(frameInstanceId: string): void {
    const frameWindow = this.frame?.nativeElement.contentWindow;
    if (!frameWindow || this.#frameInstanceId !== frameInstanceId) return;

    this.#clearOverlays();
    this.#closePort();
    const channel = this.#createChannel();
    const hostSessionId = createModernFrameId();
    this.#hostSessionId = hostSessionId;
    this.#sessionSentForHostSession = null;
    this.engineReady.set(false);
    this.sessionReady.set(false);
    this.#port = channel.port1;
    channel.port1.onmessage = (messageEvent) => {
      this.#handleChannelMessage(messageEvent.data, frameInstanceId, hostSessionId);
    };
    channel.port1.onmessageerror = () => {
      if (this.#hostSessionId === hostSessionId) this.#handleEngineFailure('CHANNEL_MESSAGE_ERROR', 'handshake');
    };
    channel.port1.start();

    frameWindow.postMessage(
      {
        protocol: MODERN_FRAME_PROTOCOL,
        version: MODERN_FRAME_VERSION,
        type: 'HOST_CONNECT',
        frameInstanceId,
        hostSessionId,
      },
      window.location.origin,
      [channel.port2],
    );
    this.#startHandshakeTimeout();
  }

  #reconnectHealthyFrame(): void {
    if (this.#frameInstanceId !== null && this.frameSrc() !== null) {
      this.#connectFrame(this.#frameInstanceId);
      return;
    }
    this.#ensureEngine();
  }

  #handleChannelMessage(data: unknown, frameInstanceId: string, hostSessionId: string): void {
    const message = parseModernFrameToHostMessageV2(data, {
      expectedFrameInstanceId: frameInstanceId,
      expectedHostSessionId: hostSessionId,
    });
    if (message === null || this.#frameInstanceId !== frameInstanceId ||
      this.#hostSessionId !== hostSessionId) {
      return;
    }

    switch (message.type) {
      case 'CHANNEL_READY':
        this.#handleChannelReady();
        break;
      case 'ROUTE_COMMITTED':
        this.#handleRouteCommitted(message);
        break;
      case 'ROUTE_FAILED':
        this.#handleRouteFailed(message);
        break;
      case 'ROUTE_DEACTIVATED':
        if (message.navigationId === this.#deactivationId) this.#deactivationId = null;
        break;
      case 'AUTHORIZE_ROUTE_REQUEST':
        this.#handleFrameAuthorization(message);
        break;
      case 'TOKEN_REFRESH_REQUEST':
        this.#handleTokenRefresh(message.requestId);
        break;
      case 'NAVIGATE_REQUEST':
        this.#handleNavigateRequest(message);
        break;
      case 'SHELL_NAVIGATE_REQUEST':
        this.#handleShellNavigateRequest(message);
        break;
      case 'TITLE_CHANGED':
        if (this.active()) {
          this.#title.setTitle(message.payload.title);
          this.frameTitle.set(message.payload.title);
        }
        break;
      case 'OVERLAY_CHANGED':
        if (this.active()) {
          this.#updateOverlay(message.payload.overlayId, message.payload.open);
          if (message.payload.open) this.#postOverlayReady(message.payload.overlayId);
        }
        break;
      case 'AUTH_REQUIRED':
        if (this.sessionReady()) this.#auth.logout('token_expired');
        break;
      case 'USER_ACTIVITY':
        if (this.sessionReady()) this.#activity.acceptFrameActivity(message.payload.timestamp);
        break;
      case 'FRAME_ERROR':
        this.#handleFrameError(message);
        break;
    }
  }

  #handleChannelReady(): void {
    this.#clearEngineTimers();
    this.engineReady.set(true);
    this.#backgroundFailureCount = 0;
    this.#waitingForOnline = false;
    this.#mark('modern-frame-engine-ready');
    this.#syncSessionContext();
    this.#tryActivateCurrentRoute();
  }

  #tryActivateCurrentRoute(): void {
    const navigationId = this.#activeNavigationId;
    if (!this.active() || navigationId === null || !this.engineReady()) return;
    this.#syncSessionContext();
    if (!this.sessionReady()) return;

    const pagePath = resolveModernFramePagePath(this.#desiredRoute);
    if (pagePath === null) {
      void this.#router.navigateByUrl('/not-found', { replaceUrl: true });
      return;
    }
    if (this.#routeAccessPolicy.hasAuthorizedGrant(pagePath)) {
      this.#activateRoute(navigationId);
      return;
    }
    if (this.#authorizationInFlightFor === navigationId) return;

    this.#authorizationInFlightFor = navigationId;
    const sessionEpoch = this.#sessionEpoch;
    this.#cancelAuthorizationSubscription();
    this.#authorizationInFlightFor = navigationId;
    this.#authorizationSubscription = this.#routeAccessPolicy.checkAccess(pagePath)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          if (!this.#isCurrentRequest(navigationId, sessionEpoch)) return;
          this.#authorizationInFlightFor = null;
          this.#authorizationSubscription = undefined;
          this.#handleAccessResult(result, pagePath, navigationId);
        },
        error: () => {
          if (!this.#isCurrentRequest(navigationId, sessionEpoch)) return;
          this.#authorizationInFlightFor = null;
          this.#authorizationSubscription = undefined;
          this.#handleAccessResult('unavailable', pagePath, navigationId);
        },
      });
  }

  #handleAccessResult(result: PageAccessResult, pagePath: string, navigationId: string): void {
    this.#routeAccessPolicy.mapModernHostResult(result, {
      onAllowed: () => {
        this.#routeAccessPolicy.grantPagePath(pagePath);
        this.#activateRoute(navigationId);
      },
      onAccessDenied: () => this.#redirectToAccessDenied(),
      onAuthRequired: () => this.#auth.logout('token_expired'),
      onUnavailable: () => {
        this.#handleActiveFailure('PAGE_ACCESS_UNAVAILABLE', 'authorization', () => {
          this.#supersedeNavigation();
          this.#tryActivateCurrentRoute();
        });
      },
    });
  }

  #activateRoute(navigationId: string): void {
    if (navigationId !== this.#activeNavigationId || !this.active() || !this.sessionReady()) return;
    const envelope = this.#channelEnvelope();
    if (envelope === null) return;

    this.#wasRevealedBeforeActivation = this.revealed();
    this.#post({
      ...envelope,
      type: 'ACTIVATE_ROUTE',
      navigationId,
      payload: {
        canonicalPath: this.#desiredRoute,
        viewportInsets: { top: SHELL_VIEWPORT_TOP_INSET_PX },
        trigger: this.#currentNavigationTrigger,
        replace: this.#currentNavigationTrigger !== 'imperative',
      },
    });
    this.#mark(this.#navigationMark('activate'));
    this.#startRouteReadyTimeout(navigationId);
  }

  #handleRouteCommitted(
    message: Extract<ModernFrameToHostMessageV2, { type: 'ROUTE_COMMITTED' }>,
  ): void {
    const committedRoute = toCanonicalModernUrl(message.payload.path);
    const parentRoute = toCanonicalModernUrl(this.#router.url);
    if (message.navigationId !== this.#activeNavigationId || committedRoute === null ||
      committedRoute !== this.#desiredRoute || committedRoute !== parentRoute || !this.active()) {
      return;
    }

    const pagePath = resolveModernFramePagePath(this.#desiredRoute);
    if (pagePath === null || !this.#routeAccessPolicy.hasAuthorizedGrant(pagePath)) {
      return;
    }

    this.#clearRouteReadyTimeout();
    this.routeReady.set(true);
    this.revealed.set(true);
    this.showLoading.set(false);
    this.#clearLoadingTimeout();
    this.#activeFailureCount = 0;
    this.#mark(this.#navigationMark('reveal'));
    this.#measure(
      'modern-frame-navigation-to-reveal',
      this.#navigationMark('intent'),
      this.#navigationMark('reveal'),
    );
    this.#measure(
      'modern-frame-activation-to-commit',
      this.#navigationMark('activate'),
      this.#navigationMark('reveal'),
    );
  }

  #handleRouteFailed(message: Extract<ModernFrameToHostMessageV2, { type: 'ROUTE_FAILED' }>): void {
    if (message.navigationId !== this.#activeNavigationId) return;
    this.#handleActiveFailure(message.payload.code, 'navigation', () => {
      this.#supersedeNavigation();
      this.#tryActivateCurrentRoute();
    });
  }

  #handleFrameAuthorization(
    message: Extract<ModernFrameToHostMessageV2, { type: 'AUTHORIZE_ROUTE_REQUEST' }>,
    attempt = 0,
  ): void {
    const pagePath = resolveModernFramePagePath(message.payload.canonicalRoute);
    const sessionEpoch = this.#sessionEpoch;
    if (!this.#authenticated || !this.sessionReady() || pagePath === null) {
      this.#postAuthorizationResult(message.requestId, pagePath === null ? 'denied' : 'auth-required');
      return;
    }

    this.#routeAccessPolicy.checkAccess(pagePath)
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (result) => {
          if (sessionEpoch !== this.#sessionEpoch) return;
          if (result === 'unavailable' && attempt < MAX_ACTIVE_RETRIES) {
            this.#handleActiveFailure(
              'PAGE_ACCESS_UNAVAILABLE',
              'authorization',
              () => this.#handleFrameAuthorization(message, attempt + 1),
              message.requestId,
            );
            return;
          }
          this.#routeAccessPolicy.mapModernHostResult(result, {
            onAllowed: () => this.#routeAccessPolicy.grantPagePath(pagePath),
            onAccessDenied: () => undefined,
            onAuthRequired: () => this.#auth.logout('token_expired'),
            onUnavailable: () => {
              if (this.active()) {
                this.#handleActiveFailure(
                  'PAGE_ACCESS_UNAVAILABLE',
                  'authorization',
                  () => undefined,
                  message.requestId,
                );
              }
            },
          });
          this.#postAuthorizationResult(message.requestId, result);
        },
        error: () => {
          if (sessionEpoch !== this.#sessionEpoch) return;
          if (attempt < MAX_ACTIVE_RETRIES) {
            this.#handleActiveFailure(
              'PAGE_ACCESS_UNAVAILABLE',
              'authorization',
              () => this.#handleFrameAuthorization(message, attempt + 1),
              message.requestId,
            );
          } else {
            this.#postAuthorizationResult(message.requestId, 'unavailable');
            this.#handleActiveFailure('PAGE_ACCESS_UNAVAILABLE', 'authorization', () => undefined, message.requestId);
          }
        },
      });
  }

  #postAuthorizationResult(requestId: string, result: ModernFrameAccessResultV2): void {
    const envelope = this.#channelEnvelope();
    if (envelope) this.#post({ ...envelope, type: 'AUTHORIZE_ROUTE_RESULT', requestId, payload: { result } });
  }

  #handleTokenRefresh(requestId: string): void {
    const sessionEpoch = this.#sessionEpoch;
    this.#auth.refreshTokens()
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe((success) => {
        if (sessionEpoch !== this.#sessionEpoch) return;
        const envelope = this.#channelEnvelope();
        if (envelope) {
          this.#post({ ...envelope, type: 'TOKEN_REFRESH_RESULT', requestId, payload: { success } });
        }
        if (!success) this.#auth.logout('token_expired');
      });
  }

  #handleNavigateRequest(message: Extract<ModernFrameToHostMessageV2, { type: 'NAVIGATE_REQUEST' }>): void {
    if (!this.active() || !this.sessionReady()) return;
    const canonicalPath = toCanonicalModernUrl(message.payload.path);
    if (canonicalPath === null || !isAllowedModernFrameRoute(canonicalPath)) return;
    void this.#router.navigateByUrl(canonicalPath, { replaceUrl: message.payload.replace ?? false });
  }

  #handleShellNavigateRequest(
    message: Extract<ModernFrameToHostMessageV2, { type: 'SHELL_NAVIGATE_REQUEST' }>,
  ): void {
    if (!this.sessionReady()) return;
    if (!isSafeShellRoute(message.payload.path)) return;
    void this.#router.navigateByUrl(message.payload.path, { replaceUrl: message.payload.replace ?? false });
  }

  #handleFrameError(message: Extract<ModernFrameToHostMessageV2, { type: 'FRAME_ERROR' }>): void {
    if (message.navigationId && message.navigationId !== this.#activeNavigationId) return;
    if (!this.active()) {
      this.#handleBackgroundFailure(message.payload.code, message.payload.scope);
      return;
    }
    this.#handleActiveFailure(
      message.payload.code,
      message.payload.scope,
      () => {
        if (message.payload.scope === 'navigation') {
          this.#supersedeNavigation();
          this.#tryActivateCurrentRoute();
        } else {
          this.#reloadDocument();
        }
      },
      message.requestId,
    );
  }

  #deactivateRoute(): void {
    this.#postDeactivate();
    this.#cancelAuthorizationSubscription();
    this.#activeNavigationId = null;
    this.#authorizationInFlightFor = null;
    this.routeReady.set(false);
    this.revealed.set(false);
    this.showLoading.set(false);
    this.#clearRouteTimers();
    this.#clearOverlays();
  }

  #postDeactivate(): void {
    const envelope = this.#channelEnvelope();
    if (!envelope || !this.engineReady()) return;
    const navigationId = createModernFrameId();
    this.#deactivationId = navigationId;
    this.#post({ ...envelope, type: 'DEACTIVATE_ROUTE', navigationId });
  }

  #supersedeNavigation(): void {
    if (!this.active()) return;
    this.#cancelAuthorizationSubscription();
    this.#clearRouteReadyTimeout();
    this.#authorizationInFlightFor = null;
    this.#activeNavigationId = createModernFrameId();
    this.routeReady.set(false);
    this.revealed.set(false);
    this.#beginLoadingPresentation();
    this.#mark(this.#navigationMark('intent'));
  }

  #redirectToAccessDenied(): void {
    this.#deactivateRoute();
    this.active.set(false);
    void this.#router.navigateByUrl('/access-denied', { replaceUrl: true });
  }

  #isCurrentRequest(navigationId: string, sessionEpoch: number): boolean {
    return this.active() && navigationId === this.#activeNavigationId && sessionEpoch === this.#sessionEpoch;
  }

  #channelEnvelope(): {
    readonly protocol: typeof MODERN_FRAME_PROTOCOL;
    readonly version: typeof MODERN_FRAME_VERSION;
    readonly frameInstanceId: string;
    readonly hostSessionId: string;
  } | null {
    if (!this.#port || !this.#frameInstanceId || !this.#hostSessionId) return null;
    return {
      protocol: MODERN_FRAME_PROTOCOL,
      version: MODERN_FRAME_VERSION,
      frameInstanceId: this.#frameInstanceId,
      hostSessionId: this.#hostSessionId,
    };
  }

  #post(message: HostToModernFrameMessageV2): void {
    this.#port?.postMessage(message);
  }

  #closePort(): void {
    if (this.#port) {
      this.#port.onmessage = null;
      this.#port.onmessageerror = null;
      this.#port.close();
      this.#port = null;
    }
    this.#hostSessionId = null;
    this.engineReady.set(false);
    this.sessionReady.set(false);
    this.#sessionSentForHostSession = null;
  }

  #handleEngineFailure(code: string, phase: 'document' | 'handshake'): void {
    this.#clearEngineTimers();
    this.#closePort();
    this.#frameInstanceId = null;
    if (this.active()) {
      this.#handleActiveFailure(code, phase, () => this.#reloadDocument());
      return;
    }
    this.#handleBackgroundFailure(code, phase);
  }

  #handleActiveFailure(
    code: string,
    phase: FrameFailurePhase,
    retry: () => void,
    requestId?: string,
  ): void {
    if (!this.active()) {
      this.#handleBackgroundFailure(code, phase, requestId);
      return;
    }

    this.#clearRouteReadyTimeout();
    this.routeReady.set(false);
    this.revealed.set(false);
    this.#beginLoadingPresentation();
    const attempt = this.#activeFailureCount + 1;
    const willRetry = this.#activeFailureCount < MAX_ACTIVE_RETRIES;
    const nextRetryDelayMs = willRetry ? (this.#timings.activeRetryDelayMs ?? 0) : undefined;
    this.#logFailure(code, phase, attempt, nextRetryDelayMs, requestId);

    if (willRetry) {
      this.#activeFailureCount += 1;
      this.#clearRetryTimeout();
      this.#retryTimeoutId = window.setTimeout(() => {
        this.#retryTimeoutId = undefined;
        retry();
      }, nextRetryDelayMs);
      return;
    }

    this.#finishActiveFailure();
  }

  #finishActiveFailure(): void {
    this.#deactivateRoute();
    this.active.set(false);
    this.#activeFailureCount = 0;
    this.#routeAccessPolicy.clearGrants();
    this.#authorizationInFlightFor = null;
    this.#discardFrameDocument();
    void this.#router.navigateByUrl('/home', { replaceUrl: true }).finally(() => {
      this.#scheduleBackgroundRecovery();
    });
  }

  #handleBackgroundFailure(code: string, phase: FrameFailurePhase, requestId?: string): void {
    this.#discardFrameDocument();
    this.#backgroundFailureCount += 1;
    const nextRetryDelayMs = this.#backgroundDelay(this.#backgroundFailureCount);
    this.#logFailure(code, phase, this.#backgroundFailureCount, nextRetryDelayMs, requestId);
    this.#scheduleBackgroundRecovery(nextRetryDelayMs);
  }

  #scheduleBackgroundRecovery(delay = this.#backgroundDelay(Math.max(1, this.#backgroundFailureCount))): void {
    this.#cancelBackgroundRecovery();
    if (!environment.modernFrameRoutesEnabled) return;
    if (!navigator.onLine) {
      this.#waitingForOnline = true;
      return;
    }
    this.#recoveryTimeoutId = window.setTimeout(() => {
      this.#recoveryTimeoutId = undefined;
      this.#reloadDocument();
    }, delay);
  }

  #cancelBackgroundRecovery(): void {
    if (this.#recoveryTimeoutId !== undefined) {
      window.clearTimeout(this.#recoveryTimeoutId);
      this.#recoveryTimeoutId = undefined;
    }
    this.#waitingForOnline = false;
  }

  #handleOnline(): void {
    if (!environment.modernFrameRoutesEnabled || this.engineReady()) return;
    if (!this.#waitingForOnline && this.#recoveryTimeoutId === undefined && this.frameSrc() !== null) return;
    this.#cancelBackgroundRecovery();
    this.#reloadDocument();
  }

  #backgroundDelay(attempt: number): number {
    const delays = this.#timings.backgroundRetryDelaysMs ?? BACKGROUND_RETRY_DELAYS_MS;
    const index = Math.min(Math.max(0, attempt - 1), delays.length - 1);
    const baseDelay = delays[index]!;
    const jitter = 0.8 + Math.random() * 0.4;
    return Math.round(baseDelay * jitter);
  }

  #discardFrameDocument(): void {
    this.#clearOverlays();
    this.#clearEngineTimers();
    this.#closePort();
    this.#frameInstanceId = null;
    this.#documentLoaded = false;
    this.#clearFrameSource();
  }

  #cancelAuthorizationSubscription(): void {
    this.#authorizationSubscription?.unsubscribe();
    this.#authorizationSubscription = undefined;
    this.#authorizationInFlightFor = null;
  }

  #clearFrameSource(): void {
    this.frameSrc.set(null);
    this.frame?.nativeElement.removeAttribute('src');
  }

  #startDocumentLoadTimeout(): void {
    this.#clearDocumentLoadTimeout();
    this.#documentLoadTimeoutId = window.setTimeout(
      () => this.#handleEngineFailure('DOCUMENT_LOAD_TIMEOUT', 'document'),
      this.#timings.documentLoadTimeoutMs,
    );
  }

  #startHandshakeTimeout(): void {
    this.#clearHandshakeTimeout();
    this.#handshakeTimeoutId = window.setTimeout(
      () => this.#handleEngineFailure('HANDSHAKE_TIMEOUT', 'handshake'),
      this.#timings.handshakeTimeoutMs ?? this.#timings.readyTimeoutMs,
    );
  }

  #startRouteReadyTimeout(navigationId: string): void {
    this.#clearRouteReadyTimeout();
    this.#routeReadyTimeoutId = window.setTimeout(() => {
      if (navigationId !== this.#activeNavigationId) return;
      if (this.#wasRevealedBeforeActivation && this.#isEngineWarm() &&
        this.#activeFailureCount < MAX_ACTIVE_RETRIES) {
        this.#activeFailureCount += 1;
        this.#clearRouteReadyTimeout();
        this.#logFailure('ROUTE_READY_TIMEOUT', 'navigation', this.#activeFailureCount, 0);
        this.#tryActivateCurrentRoute();
        return;
      }
      this.#handleActiveFailure('ROUTE_READY_TIMEOUT', 'navigation', () => {
        this.#supersedeNavigation();
        this.#tryActivateCurrentRoute();
      });
    }, this.#timings.readyTimeoutMs);
  }

  #beginLoadingPresentation(): void {
    this.showLoading.set(false);
    this.#clearLoadingTimeout();
    this.#loadingTimeoutId = window.setTimeout(() => {
      this.#loadingTimeoutId = undefined;
      if (this.active() && !this.routeReady()) this.showLoading.set(true);
    }, this.#timings.loadingDelayMs ?? 150);
  }

  #resetRoutePresentation(): void {
    this.#activeNavigationId = null;
    this.#authorizationInFlightFor = null;
    this.routeReady.set(false);
    this.revealed.set(false);
    this.showLoading.set(false);
    this.frameTitle.set(DEFAULT_FRAME_TITLE);
    this.#clearRouteTimers();
    this.#clearOverlays();
  }

  #clearDocumentLoadTimeout(): void {
    if (this.#documentLoadTimeoutId === undefined) return;
    window.clearTimeout(this.#documentLoadTimeoutId);
    this.#documentLoadTimeoutId = undefined;
  }

  #clearHandshakeTimeout(): void {
    if (this.#handshakeTimeoutId === undefined) return;
    window.clearTimeout(this.#handshakeTimeoutId);
    this.#handshakeTimeoutId = undefined;
  }

  #clearRouteReadyTimeout(): void {
    if (this.#routeReadyTimeoutId === undefined) return;
    window.clearTimeout(this.#routeReadyTimeoutId);
    this.#routeReadyTimeoutId = undefined;
  }

  #clearLoadingTimeout(): void {
    if (this.#loadingTimeoutId === undefined) return;
    window.clearTimeout(this.#loadingTimeoutId);
    this.#loadingTimeoutId = undefined;
  }

  #clearRetryTimeout(): void {
    if (this.#retryTimeoutId === undefined) return;
    window.clearTimeout(this.#retryTimeoutId);
    this.#retryTimeoutId = undefined;
  }

  #clearEngineTimers(): void {
    this.#clearDocumentLoadTimeout();
    this.#clearHandshakeTimeout();
  }

  #clearRouteTimers(): void {
    this.#clearRouteReadyTimeout();
    this.#clearLoadingTimeout();
    this.#clearRetryTimeout();
  }

  #clearAllTimers(): void {
    this.#clearEngineTimers();
    this.#clearRouteTimers();
    this.#cancelBackgroundRecovery();
  }

  #updateOverlay(overlayId: string, open: boolean): void {
    if (open) this.#activeOverlayIds.add(overlayId);
    else this.#activeOverlayIds.delete(overlayId);
    this.#syncOverlayClass();
  }

  #clearOverlays(): void {
    this.#activeOverlayIds.clear();
    this.#syncOverlayClass();
  }

  #syncOverlayClass(): void {
    const active = this.#activeOverlayIds.size > 0;
    this.overlayActive.set(active);
    if (active) this.#renderer.addClass(this.#host.nativeElement, 'modern-host--overlay-active');
    else this.#renderer.removeClass(this.#host.nativeElement, 'modern-host--overlay-active');
  }

  #postOverlayReady(overlayId: string): void {
    requestAnimationFrame(() => {
      const envelope = this.#channelEnvelope();
      if (envelope) this.#post({ ...envelope, type: 'OVERLAY_READY', payload: { overlayId } });
    });
  }

  #logFailure(
    code: string,
    phase: FrameFailurePhase,
    attempt: number,
    nextRetryDelayMs?: number,
    requestId?: string,
  ): void {
    const canonical = toCanonicalModernUrl(this.#desiredRoute);
    const routePathname = canonical === null ? null : getModernFramePathname(canonical);
    console.error('[modern-frame]', {
      code,
      phase,
      routePathname,
      navigationId: this.#activeNavigationId,
      requestId: requestId ?? null,
      frameInstanceId: this.#frameInstanceId,
      attempt,
      nextRetryDelayMs: nextRetryDelayMs ?? null,
    });
  }

  #navigationMark(phase: string): string {
    return `modern-frame-${phase}:${this.#activeNavigationId ?? 'none'}`;
  }

  #mark(name: string): void {
    window.performance?.mark(name);
  }

  #measure(name: string, startMark: string, endMark: string): void {
    try {
      window.performance?.measure(name, startMark, endMark);
    } catch {
      // Marks can be superseded by rapid navigation or a frame-document retry.
    }
  }
}
